import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { authHeaders } from "@/lib/authToken";

type Coverage = {
  documents: { total: number; embedded: number };
  threads: { total: number; embedded: number };
  communications: Record<string, number>;
  githubRepos: number;
  driveImported: number;
  hint: string;
};

export function LibrarianCoverageCard() {
  const query = useQuery({
    queryKey: ["librarian-coverage"],
    queryFn: async () => {
      const headers = await authHeaders();
      const res = await fetch("/api/v1/librarian/coverage", { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message ?? "Coverage failed");
      return body as Coverage;
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Librarian coverage</CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-2">
        {query.isLoading ? (
          <p className="text-muted-foreground">Checking what is embedded…</p>
        ) : query.isError ? (
          <p className="text-destructive">{(query.error as Error).message}</p>
        ) : query.data ? (
          <>
            <p>
              Library: {query.data.documents.embedded}/{query.data.documents.total} embedded
            </p>
            <p>
              Threads: {query.data.threads.embedded}/{query.data.threads.total} embedded
            </p>
            <p>Drive imports in Library: {query.data.driveImported}</p>
            <p>Indexed GitHub repos: {query.data.githubRepos}</p>
            <p className="text-muted-foreground">
              Communications indexed:{" "}
              {Object.entries(query.data.communications)
                .filter(([, n]) => n > 0)
                .map(([k, n]) => `${k} ${n}`)
                .join(", ") || "none yet"}
            </p>
            <p className="text-xs text-muted-foreground">{query.data.hint}</p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
